import { BaseOption } from "./form";
import voicesData from "./voices.json";

export type VoiceGender = "m" | "f";
export type VoiceRange = "Warm" | "Soft" | "Bright" | "Grounded" | "Resonant";
export type VoiceAccent =
  | "American"
  | "British"
  | "Indian"
  | "Australian"
  | "Spanish"
  | "Chinese"
  | "Arabic";

// Must stay in sync with the backend `TTSProvider` enum
// (backend/apps/tutor/pipeline/utils/cloud_models.py); enforced by
// backend/tests/test_tts_provider_contract.py.
export type TTSProvider = "cartesia" | "inworld" | "xai";

export interface AudioVoice {
  id: string;
  name: string;
  gender: VoiceGender;
  range: VoiceRange;
  accent: VoiceAccent;
  tone: string;
  pitch: string;
  audioPreviews: Record<string, string>;
  speedOptions: BaseOption[];
  defaultSpeed: string;
  localizedNames?: Record<string, string>;
  ttsProvider: TTSProvider; // sent to the backend as voice_model
  providerVoiceId: string; // sent to the backend as voice_id
  // Rollout flag only (hides previews, gates behind enableBetaTtsProviders) — not a schema variant.
  isBeta?: boolean;
}

export interface AudioVoiceFormData {
  voiceId: string;
  speed: string;
}

const defaultSpeedOptions: BaseOption[] = [
  { value: "slow", label: "Slow" },
  { value: "medium", label: "Medium" },
  { value: "fast", label: "Fast" },
];

function makeAudioPreviews(name: string): Record<string, string> {
  return {
    slow: `/voice_options_audio/${name}_slow.webm`,
    medium: `/voice_options_audio/${name}.webm`,
    fast: `/voice_options_audio/${name}_fast.webm`,
  };
}

export function getLocalizedName(voice: AudioVoice, langCode: string): string {
  return voice.localizedNames?.[langCode] ?? voice.name;
}

// JSON record = data only; audioPreviews/speedOptions are re-attached in code.
type RawVoice = Omit<AudioVoice, "audioPreviews" | "speedOptions">;

// Compile-time shape guard for voices.json: every record must carry these
// required string fields (a missing one is a build error, not a silent
// undefined at runtime). Value validity — e.g. gender ∈ "m"|"f" — is a stricter
// union than JSON can express, so it's asserted at runtime in the catalog test.
type VoicesJson = {
  voices: ReadonlyArray<
    Record<
      | "id"
      | "name"
      | "gender"
      | "range"
      | "accent"
      | "tone"
      | "pitch"
      | "defaultSpeed",
      string
    > & {
      isBeta: boolean;
      ttsProvider: string;
      providerVoiceId: string;
      localizedNames?: Record<string, string>;
    }
  >;
  languageVoiceOrder: Record<string, string[]>;
};

const catalog = voicesData satisfies VoicesJson;

const capitalize = (s: string): string =>
  s.charAt(0).toUpperCase() + s.slice(1);

function hydrateVoice(raw: RawVoice): AudioVoice {
  return {
    ...raw,
    // Preview files are keyed by the capitalized id — a stable ASCII slug that
    // matches the on-disk S3 filenames. The display `name` is deliberately not
    // used: it is localizable/renamable and can carry accents, so it is the
    // wrong thing to key storage on. Beta voices have no previews yet.
    audioPreviews: raw.isBeta ? {} : makeAudioPreviews(capitalize(raw.id)),
    speedOptions: defaultSpeedOptions,
  };
}

// gender/accent/etc. are typed as string in JSON but as literal unions on
// RawVoice; the single cast bridges that (values are runtime-validated).
const ALL_VOICES: AudioVoice[] = (catalog.voices as unknown as RawVoice[]).map(
  hydrateVoice,
);

export const AUDIO_VOICES: AudioVoice[] = ALL_VOICES.filter((v) => !v.isBeta);

// Beta voices are kept out of LANGUAGE_VOICE_ORDER — appended only for flagged
// users via getBetaVoicesForLanguage().
export const BETA_AUDIO_VOICES: AudioVoice[] = ALL_VOICES.filter(
  (v) => v.isBeta,
);

export const AUDIO_VOICE_MAP: Record<string, AudioVoice> = Object.fromEntries(
  ALL_VOICES.map((v) => [v.id, v]),
);

export const DEFAULT_AUDIO_VOICE: AudioVoice = AUDIO_VOICE_MAP["chloe"];

export const VALID_VOICE_IDS = ALL_VOICES.map((v) => v.id);

export const LANGUAGE_VOICE_ORDER: Record<string, string[]> =
  catalog.languageVoiceOrder;

export function getVoicesForLanguage(languageCode: string): AudioVoice[] {
  const langIds = LANGUAGE_VOICE_ORDER[languageCode];
  if (langIds) {
    return langIds
      .map((id) => AUDIO_VOICE_MAP[id])
      .filter((v): v is AudioVoice => v != null);
  }
  return AUDIO_VOICES;
}

// Beta voices to append for flag-enabled users (English-only for now).
export function getBetaVoicesForLanguage(languageCode: string): AudioVoice[] {
  return languageCode === "en" ? BETA_AUDIO_VOICES : [];
}

// Providers the backend actually accepts for name pronunciation. Must stay in
// sync with `Literal["cartesia"]` on SaveNamePronunciationEntryRequest.provider in
// backend/apps/web/api/v1/name_pronunciation.py; enforced by
// backend/tests/test_name_pronunciation_provider_contract.py.
// Beta voices outside this set are selectable in voice pickers but their
// pronunciation training silently no-ops, so pickers that expose training
// should filter beta voices through this first.
export const NAME_PRONUNCIATION_SUPPORTED_PROVIDERS: readonly TTSProvider[] = [
  "cartesia",
];

export function supportsNamePronunciationTraining(voice: AudioVoice): boolean {
  return NAME_PRONUNCIATION_SUPPORTED_PROVIDERS.includes(voice.ttsProvider);
}

export function getDefaultVoiceForLanguage(languageCode: string): AudioVoice {
  const voices = getVoicesForLanguage(languageCode);
  return voices[0] ?? DEFAULT_AUDIO_VOICE;
}

// The voice to use when switching to `languageCode`: the current one if that
// language offers it, otherwise the language's default. Every place that changes
// the selected language has to run this, or the student is left on a voice the
// new language cannot speak.
//
// `includeBeta` must match what the picker offered. Beta voices are kept out of
// LANGUAGE_VOICE_ORDER and appended separately for flagged users, so without it
// a voice ChangeVoiceModal legitimately offered is judged invalid here and the
// student is swapped off it. The fallback stays non-beta — a beta voice is never
// a language's default.
export function resolveVoiceForLanguage(
  languageCode: string,
  currentVoiceId: string | undefined,
  { includeBeta = false }: { includeBeta?: boolean } = {},
): string {
  const voices = includeBeta
    ? [
        ...getVoicesForLanguage(languageCode),
        ...getBetaVoicesForLanguage(languageCode),
      ]
    : getVoicesForLanguage(languageCode);
  if (currentVoiceId && voices.some((v) => v.id === currentVoiceId)) {
    return currentVoiceId;
  }
  return getDefaultVoiceForLanguage(languageCode).id;
}

// Resolve a catalog voice id to what the backend runs: the concrete provider
// voice id (voice_id) and the provider. Internally this is one canonical
// TTSProvider value; callers building a wire request map it onto both the
// voice_model and voice_provider params the backend/gateway expect there.
export const getSessionVoiceConfig = (
  voiceId: string,
): { voiceId: string; ttsProvider: TTSProvider } => {
  const voice = AUDIO_VOICE_MAP[voiceId] ?? DEFAULT_AUDIO_VOICE;
  return {
    voiceId: voice.providerVoiceId,
    ttsProvider: voice.ttsProvider,
  };
};

// Gender of the picked catalog voice ("m"/"f"), sent to the backend as voice_gender.
export const getVoiceGender = (voiceId: string): VoiceGender | undefined =>
  AUDIO_VOICE_MAP[voiceId]?.gender;

/** Get the TTS provider for a voice ("cartesia" | "inworld" | "xai"). */
export const getVoiceProvider = (voice: AudioVoice): TTSProvider =>
  voice.ttsProvider;

/** Get the provider-specific voice ID (Cartesia UUID, Inworld ID, etc). */
export const getProviderVoiceId = (voice: AudioVoice): string =>
  voice.providerVoiceId;

/** Build the name-pronunciation entry key: '{provider}:{providerVoiceId}:{lang}'. */
export const getNamePronunciationKey = (
  voice: AudioVoice,
  lang: string,
): string => `${getVoiceProvider(voice)}:${getProviderVoiceId(voice)}:${lang}`;

export const getDefaultSpeed = (
  voice: AudioVoice,
  formData: AudioVoiceFormData | undefined,
): string => {
  if (!formData) return voice.defaultSpeed;
  return voice.id === formData.voiceId ? formData.speed : voice.defaultSpeed;
};

export interface LegacyVoiceOption {
  value: string;
  voiceId: string;
  label: string;
  icon: string;
  traits: string[];
  audio: Record<string, string>;
  speedOptions: BaseOption[];
  defaultSpeed: string;
}

export function toVoicePreviewOption(
  voice: AudioVoice,
  langCode?: string,
): LegacyVoiceOption {
  const name = langCode ? getLocalizedName(voice, langCode) : voice.name;
  return {
    value: voice.id,
    voiceId: voice.providerVoiceId,
    label: name,
    icon: "",
    traits: [voice.range, voice.accent, voice.tone],
    audio: voice.audioPreviews,
    speedOptions: voice.speedOptions,
    defaultSpeed: voice.defaultSpeed,
  };
}
